import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    EventEmitter,
    QueryList,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { HamSimpleDropDownDirective } from '@modeso/aumico-components';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { MarkNotRelevantEvent } from '../../types/closing-documents.types';
import { DocumentUpstreamState } from '../../types/closing-documents.types';
import { AccountDocument } from './account-documents.types';
import { AbcpDocumentViewerSlotDirective } from '../document-detail/document-detail-slots.directive';

//REV-21363 — the account's `Dokumente` area (Pure View). Contract: e2e-lab/account-documents.lab.spec.ts.
//
//SKELETON: the I/O below is final, the template is deliberately empty — the contract is red first.
//
//Dumb by construction (ADR-0009 §10): no store, no HTTP, no persistence. Every control EMITS;
//nothing self-applies. The container is REV-21376 (demo surface); a production host is REV-21379
//and NOT in this sprint — this area is verified in the Component-Lab.
//
//Authorities, in this order:
//  1. ADR-0009 (accepted 11.08.2026) — beats the prototype wherever both speak.
//  2. The LIVE prototype (Konten → an account → `Dokumente`) — layout, order, German labels.
//     1021 = two documents (the dropdown), 1000 = exactly one (static name), 1022 = none (upload).
//  3. The PNGs attached to the ticket — older iterations, not the contract.
//
//Where (1) overrules the prototype:
//  · NO delete control (§4) — the prototype still has a locked trash button whose tooltip reads
//    "Löschen gesperrt: Abstimmungsgrundlage für Konto 1021". There is no `deleteLockReason` input
//    either: the whole apparatus goes, not just its enabled state.
//  · the upload input is PDF-only (§12) — the prototype allows `application/pdf,image/*`.

@Component({
    selector: 'abcp-account-documents',
    templateUrl: './account-documents.component.html',
    styleUrls: ['./account-documents.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpAccountDocumentsComponent implements OnChanges, AfterContentInit {
    /**
     * REV-21369 — the projected PDF viewer (ADR-0021 E6, extended to the account pane on 04.10.2026).
     * Present ⇒ it replaces the preview for the selected document. Same slot directive as the
     * single-document page: this view never renders the viewer and never fetches bytes.
     */
    @ContentChildren(AbcpDocumentViewerSlotDirective) private viewerSlots?: QueryList<AbcpDocumentViewerSlotDirective>;

    /**
     * ⚠️ A QueryList plus `markForCheck`, not a plain `@ContentChild`: the host shows the viewer only
     * once the byte door answered, and withdraws it again on a 404. This view is OnPush, and a
     * projection that comes or goes does not mark it dirty — it would keep the empty viewer frame and
     * hide the preview.
     */
    get viewerSlot(): boolean {
        return (this.viewerSlots?.length ?? 0) > 0;
    }

    constructor(private readonly cdr: ChangeDetectorRef) {}

    ngAfterContentInit(): void {
        this.viewerSlots?.changes.subscribe(() => this.cdr.markForCheck());
    }

    /** The documents of THIS account. Empty renders the upload area, not an error. */
    @Input() documents: AccountDocument[] = [];

    /**
     * CONTROLLED: the component never writes this. See `onSelectionChange` — `hamSimpleDropDown` owns its
     * own value, so the control is pushed back to this input after every pick.
     */
    @Input() selectedDocumentId: string | null = null;

    @Input() loading = false;
    @Input() error = false;

    /** The chosen document's id. Emitted only; `selectedDocumentId` is not touched. */
    @Output() documentSelect = new EventEmitter<string>();

    /** The upload control. The area opens no modal itself — the container owns that (ADR-0009 §11). */
    @Output() uploadRequested = new EventEmitter<void>();

    /**
     * The eye. One direction only: a document already marked as not relevant is not in an
     * account's document list, so this area can never offer the reversal. The hub and the
     * single-document page own that direction.
     */
    @Output() markNotRelevant = new EventEmitter<MarkNotRelevantEvent>();

    /**
     * The dropdown is `hamSimpleDropDown` — the SAME control the pane header right above this area
     * uses for its `Bereich` select (`account-workspace.component.html`), so the two rows match.
     *
     * ⚠️ It is deliberately NOT `aum-select`, which the ticket originally named. `aum-select` is a
     * `mat-form-field appearance="outline"`: in this 288 px toolbar it rendered 86 px tall against
     * the 28 px of the rest of the row, overflowed its flex slot to 204 px, and its
     * `.mat-form-field-flex` then covered the upload button — `elementFromPoint` on that button
     * returned the form field, so the action was unreachable, not merely ugly. Search survives the
     * swap: `optionKeys` is what `hamSimpleDropDown` filters on.
     */
    @ViewChild(HamSimpleDropDownDirective) private dropdown?: HamSimpleDropDownDirective;

    /** What the dropdown displays. Derived from `selectedDocumentId`, never authored here. */
    documentModel: string | null = null;

    /**
     * What the dropdown lists. `optionKeys: ['name']` is both what it SHOWS and what it filters on.
     *
     * ⚠️ A FIELD, recomputed only when `documents` actually changes — deliberately not a getter.
     * A getter would hand the directive a new array reference on every change-detection cycle, its
     * own `ngOnChanges` would fire each time and rewrite the input from the selected option, and
     * the user's search text would be wiped after every keystroke.
     */
    options: INameId[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['documents']) {
            this.options = this.documents.map((document) => ({ id: document.id, name: document.filename }));
        }
        this.syncControl();
    }

    /**
     * REV-21964 · C1–C4 — the marker's tooltip, as a TOTAL map.
     *
     * ⚠️ Not a concatenated key. The states are camelCase and the i18n keys snake_case, so
     * `'abcp_upstream_state_' + state` asks for a key that exists in no locale — and a missing key
     * does not throw, it renders the identifier at the trustee. That exact defect shipped in the hub
     * and was caught in review; the same mistake is not repeated here.
     */
    upstreamStateLabelKey(state: DocumentUpstreamState | undefined): string {
        switch (state) {
            case 'deleted':
                return 'abcp_upstream_state_deleted';
            case 'movedOut':
                return 'abcp_upstream_state_moved_out';
            case 'replaced':
                return 'abcp_upstream_state_replaced';
            default:
                return '';
        }
    }

    /** Whether the SELECTED document carries a marker. `present`/absent is the quiet, normal case. */
    get hasUpstreamMarker(): boolean {
        const state = this.selectedDocument?.upstreamState;
        return state != null && state !== 'present';
    }

    get selectedDocument(): AccountDocument | null {
        return this.documents.find((document) => document.id === this.selectedDocumentId) ?? null;
    }

    get hasDocuments(): boolean {
        return this.documents.length > 0;
    }

    get isSingleDocument(): boolean {
        return this.documents.length === 1;
    }

    onUploadClick(): void {
        this.uploadRequested.emit();
    }

    /**
     * Marks at once, without a confirmation: nothing is deleted, and the Dokumente tab takes the
     * mark back. There is no reversal here, because a marked document is no longer in an
     * account's document list.
     */
    onMarkNotRelevant(): void {
        const document = this.selectedDocument;
        if (document) this.markNotRelevant.emit({ documentId: document.id, notRelevant: true });
    }

    /**
     * Emit, then put the display BACK where the input says it should be.
     *
     * The dropdown owns its own value, like any `ControlValueAccessor`: without the reset the area
     * would show a selection nobody applied, on a click the container may never answer. If the
     * container does answer, the new `@Input()` arrives and `ngOnChanges` moves the display
     * forward. That is what makes "emits without mutating `selectedDocumentId`" a testable claim
     * rather than a comment.
     *
     * `writeValue` directly rather than via the bound model: re-assigning the SAME value changes
     * nothing for Angular, so `ngModel` would never push it back down.
     */
    onSelectionChange(documentId: string | null): void {
        // Bound to `selectionChange`, which only fires on a real pick and already carries the
        // `returnKey` id. The guard stays anyway: a view must never ask its container to select a
        // document that is not in the list it was given.
        if (documentId && this.documents.some((document) => document.id === documentId)) {
            this.documentSelect.emit(documentId);
        }
        this.syncControl();
    }

    private syncControl(): void {
        this.documentModel = this.selectedDocumentId;
        this.dropdown?.writeValue(this.selectedDocumentId);
    }
}
